import { UserRole } from "@features/settings/types/settings.types";
import { ConfirmationModal } from "@shared/components/ConfirmationModal";
import ModalBlank from "@shared/components/modals/ModalBlank";
import { Button } from "@shared/components/ui/Button";
import {
  ArrowLeftSmallIcon,
  CheckCircleIcon,
  EmptyCircleIcon,
  HeartIcon,
  SmallCheckIcon,
  SpinnerIcon,
} from "@shared/components/ui/Icon";
import { getProfileImage, useProfile } from "@shared/hooks/useProfile";
import AuthenticatedLayout from "@shared/layouts/AuthenticatedLayout";
import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import EventForm from "../components/EventForm";
import EventFullDetails from "../components/EventFullDetails";
import { useCalendar } from "../hooks/useCalendar";
import { CalendarService } from "../services/calendar.service";
import {
  setEventStatus as eventFavoriteSetEventStatus,
  listInviteableMembers,
} from "../services/eventFavorite.service";
import { EventCategory } from "../types/calendar.types";

const EventDetailsPage: React.FC = () => {
  const calendar = useCalendar();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { events, categories, updateEvent, deleteEvent, fetchCalendarData } =
    calendar;
  const event = events.find((e) => e.id === id);
  const category: EventCategory | undefined = event
    ? categories.find((cat) => cat.id === event.categoryId)
    : undefined;

  // Attendees and user status directly from the event
  const attendees = event?.attendees || [];
  const loadingAttendees = false; // Already comes with the event
  // refreshAttendees is no longer needed
  // Inviteable members state
  const [inviteableMembers, setInviteableMembers] = useState<
    Array<{ id: string; name: string; avatarUrl: string }>
  >([]);
  const [loadingInviteables, setLoadingInviteables] = useState(true);
  // Current profile
  const { user, isLoading: loadingProfile } = useProfile();

  // Edit modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Delete confirmation modal
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Edit permission
  const canEdit =
    (user && event && event.createdBy === user.id) ||
    (user && user.role === UserRole.ADMIN);

  // Favorite and attending directly from the event
  const [attendingLoading, setAttendingLoading] = useState(false);
  const [favLoading, setFavLoading] = useState(false);
  const isFavorite = !!event?.userStatus?.isFavorite;
  const isAttending = event?.userStatus?.isAttendee || false;

  // Updates attendance and refreshes global events.
  const handleToggleAttending = async () => {
    if (!event || !user) return;
    setAttendingLoading(true);
    try {
      await eventFavoriteSetEventStatus(event.id, {
        isAttendee: !isAttending,
        isFavorite: isFavorite, // keep current value
      });
      await fetchCalendarData(true);
    } catch (e) {
      // You can display an error toast
    } finally {
      setAttendingLoading(false);
    }
  };

  // Updates favorite and refreshes global events.
  const handleToggleFavorite = async () => {
    if (!event || !user) return;
    setFavLoading(true);
    try {
      await eventFavoriteSetEventStatus(event.id, {
        isFavorite: !isFavorite,
        isAttendee: isAttending, // keep current value
      });
      await fetchCalendarData(true);
    } catch (e) {
      // You can display an error toast
    } finally {
      setFavLoading(false);
    }
  };

  // Fetch inviteable members from backend
  React.useEffect(() => {
    const fetchInviteables = async () => {
      if (!id) return;
      setLoadingInviteables(true);
      try {
        const data = await listInviteableMembers(id);
        setInviteableMembers(data);
      } finally {
        setLoadingInviteables(false);
      }
    };
    fetchInviteables();
  }, [id]);

  // Local state for invited users in this session
  const [invitedIds, setInvitedIds] = useState<string[]>([]);

  // Handler for inviting a user to an event
  const handleInvite = async (inviteeId: string) => {
    if (!event) return;
    try {
      await CalendarService.inviteToEvent(event.id, inviteeId);
      setInvitedIds((prev) => [...prev, inviteeId]);
      // Optional: visual feedback, you can use a toast or alert
      // alert("Invited successfully!");
    } catch (err) {
      alert("Error inviting");
    }
  };

  // Edit event
  const handleUpdateEvent = async (data: any) => {
    if (!id) return;
    setIsSubmitting(true);
    try {
      await updateEvent(id, data);
      setShowEditModal(false);
    } catch (e) {
      // Error is handled in the hook
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show delete confirmation modal
  const handleDeleteClick = () => {
    setShowDeleteConfirmation(true);
  };

  // Handle event delete
  const handleDeleteEvent = async () => {
    if (!id) return;

    setIsDeleting(true);
    try {
      await deleteEvent(id);
      // Navigation will happen automatically since the event is removed from the store
      navigate("/calendar");
    } catch (e) {
      // Error is handled in the hook
      setShowDeleteConfirmation(false);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <AuthenticatedLayout
      pageHeader={{
        title: " ",
      }}
      isLoading={false}
      error={null}
    >
      <div className="max-w-5xl mx-auto flex flex-col lg:flex-row lg:space-x-8 xl:space-x-16">
        {/* Content */}
        <div className="flex-1">
          {/* Back button */}
          <div className="mb-6">
            <button
              className="btn-sm px-3 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700/60 hover:border-gray-300 dark:hover:border-gray-600 text-gray-800 dark:text-gray-300"
              onClick={() => navigate("/calendar")}
            >
              <ArrowLeftSmallIcon className="fill-current text-gray-400 dark:text-gray-500 mr-2" />
              <span>Back To Events</span>
            </button>
          </div>
          {event && (
            <EventFullDetails
              event={event}
              category={category}
              onClose={() => navigate("/calendar")}
            />
          )}
        </div>
        {/* Sidebar */}
        <div className="space-y-4 lg:w-72 xl:w-80 mt-8 lg:mt-0">
          {/* Action buttons (Attending, Favorite) */}
          <div className="bg-white dark:bg-gray-800 p-5 shadow-sm rounded-xl">
            <div className="space-y-2">
              <button
                className={`btn w-full rounded-2xl bg-gray-900 text-gray-100 hover:bg-gray-800 dark:bg-gray-100 dark:text-gray-800 dark:hover:bg-white flex items-center justify-center ${
                  attendingLoading ? "opacity-60 pointer-events-none" : ""
                }`}
                onClick={handleToggleAttending}
                disabled={attendingLoading || !user || !event}
                aria-pressed={isAttending}
                title={
                  isAttending ? "Mark as not attending" : "Mark as attending"
                }
              >
                {attendingLoading ? (
                  <SpinnerIcon className="animate-spin mr-2" />
                ) : isAttending ? (
                  <CheckCircleIcon className="fill-current shrink-0 text-green-500" />
                ) : (
                  <EmptyCircleIcon className="fill-current shrink-0 text-gray-400" />
                )}
                <span className="ml-1">
                  {isAttending ? "Attending" : "Attend"}
                </span>
              </button>
              {event?.url && isAttending && (
                <Button
                  variant="success"
                  fullWidth
                  as="a"
                  href={event?.url}
                  target="_blank"
                >
                  Link to Join
                </Button>
              )}
              <button
                className={`btn w-full rounded-2xl border-gray-200 dark:border-gray-700/60 hover:border-gray-300 dark:hover:border-gray-600 text-gray-800 dark:text-gray-300 flex items-center justify-center ${
                  favLoading ? "opacity-60 pointer-events-none" : ""
                }`}
                onClick={handleToggleFavorite}
                disabled={favLoading || !user || !event}
                aria-pressed={isFavorite}
                title={
                  isFavorite ? "Remove from favorites" : "Add to favorites"
                }
              >
                {favLoading ? (
                  <SpinnerIcon className="animate-spin mr-2" />
                ) : (
                  <HeartIcon
                    filled={isFavorite}
                    size={16}
                    className={
                      isFavorite ? "fill-red-500 shrink-0" : "shrink-0"
                    }
                  />
                )}
                <span className="ml-2">Favorite</span>
              </button>
              {/* Edit event button for admin/creator only */}
              {canEdit && (
                <div className="space-y-2">
                  <Button
                    variant="primary"
                    fullWidth
                    onClick={() => setShowEditModal(true)}
                  >
                    Edit event
                  </Button>
                  <Button
                    variant="danger"
                    fullWidth
                    onClick={handleDeleteClick}
                  >
                    Delete event
                  </Button>
                </div>
              )}
            </div>
          </div>
          {/* Attendees */}
          <div className="bg-white dark:bg-gray-800 p-5 shadow-sm rounded-xl">
            <div className="text-sm text-gray-800 dark:text-gray-100 font-semibold mb-5">
              Attendees ({attendees.length})
            </div>
            {attendees.length > 0 ? (
              <ul className="space-y-3">
                {attendees.map((att) => (
                  <li key={att.userId} className="flex items-center">
                    <Link
                      to={`/community/profile/${att.userId}`}
                      className="flex items-center group"
                    >
                      <img
                        className="w-8 h-8 rounded-full mr-3 group-hover:ring-2 group-hover:ring-blue-400 transition"
                        src={getProfileImage(att)}
                        alt={att.name || att.userId}
                      />
                      <span className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate group-hover:text-blue-600 dark:group-hover:text-blue-400 transition">
                        {att.name || att.userId}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-gray-400 text-xs">No attendees yet.</div>
            )}
          </div>
          {/* Invite Friends */}
          <div className="bg-white dark:bg-gray-800 p-5 shadow-sm rounded-xl">
            <div className="text-sm text-gray-800 dark:text-gray-100 font-semibold mb-5">
              Invite Friends ({inviteableMembers?.length})
            </div>
            {loadingInviteables ? (
              <div className="text-gray-400 text-xs">Loading friends...</div>
            ) : inviteableMembers && inviteableMembers.length > 0 ? (
              <ul className="space-y-3">
                {inviteableMembers.map((member) => (
                  <li
                    key={member.id}
                    className="flex items-center justify-between"
                  >
                    <Link
                      to={`/community/profile/${member.id}`}
                      className="grow flex items-center group"
                    >
                      <img
                        className="w-8 h-8 rounded-full mr-3 group-hover:ring-2 group-hover:ring-blue-400 transition"
                        src={getProfileImage(member)}
                        alt={member.name}
                      />
                      <span className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate group-hover:text-blue-600 dark:group-hover:text-blue-400 transition">
                        {member.name}
                      </span>
                    </Link>
                    <button
                      className={`btn-xs text-xs border-gray-200 dark:border-gray-700/60 hover:border-gray-300 dark:hover:border-gray-600 px-2.5 py-1 rounded-full shadow-none flex items-center ${
                        invitedIds.includes(member.id)
                          ? "bg-green-100 border-green-400 text-green-700 cursor-default"
                          : "text-gray-800 dark:text-gray-300"
                      }`}
                      onClick={() =>
                        !invitedIds.includes(member.id) &&
                        handleInvite(member.id)
                      }
                      disabled={invitedIds.includes(member.id)}
                    >
                      {invitedIds.includes(member.id) ? (
                        <>
                          <SmallCheckIcon className="inline-block mr-1 text-green-500" />
                          Invited
                        </>
                      ) : (
                        "Invite"
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-gray-400 text-xs">No friends to invite.</div>
            )}
          </div>
        </div>
      </div>
      {/* Event Edit Modal */}
      {event && (
        <ModalBlank
          id="event-edit-modal"
          className="max-h-[80vh] overflow-y-auto p-4"
          modalOpen={showEditModal}
          setModalOpen={setShowEditModal}
          showCloseButton={true}
        >
          <EventForm
            event={event}
            categories={categories}
            onSubmit={handleUpdateEvent}
            onCancel={() => setShowEditModal(false)}
            isSubmitting={isSubmitting}
            canEdit={canEdit}
            canDelete={false}
          />
        </ModalBlank>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={handleDeleteEvent}
        title="Delete Event"
        message="Are you sure you want to delete this event? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        isLoading={isDeleting}
      />
    </AuthenticatedLayout>
  );
};

export default EventDetailsPage;
